// "Discussions" — conversation list, contact invitations (host and guest sides).

import { getConversations, getSpace } from '../space/current';
import { InviteHost, acceptInvite, type InviteRequest } from '../contacts/invite';
import type { Conversations } from '../contacts/conversations';
import type { Conversation } from '../contacts/conversation';
import { SECRET } from '../core/ids';
import { h, keep } from './dom';
import { copyText, iceServers, keepAwake, qrElement, wsUrl } from './common';
import { relativeDate } from './notes';
import type { View } from './view';

/** Is this person connected to one of my devices right now (any of their devices)? */
export function personOnline(c: Conversation, person: string): boolean {
  const online = c.sync?.onlineDevices() ?? new Set<string>();
  return c.devicesOf(person).some((d) => online.has(d));
}

export function personLastSeen(c: Conversation, person: string): number | null {
  const times = c.devicesOf(person).map((d) => c.sync?.lastSeen.get(d) ?? 0).filter(Boolean);
  return times.length ? Math.max(...times) : null;
}

/** Is anyone else of the conversation online right now? */
export function contactOnline(c: Conversation): boolean {
  return c.others().some((p) => personOnline(c, p));
}

export function contactLastSeen(c: Conversation): number | null {
  const times = c.others().map((p) => personLastSeen(c, p) ?? 0).filter(Boolean);
  return times.length ? Math.max(...times) : null;
}

function pasteInvite(): HTMLElement {
  const field = h('input', { class: 'field', placeholder: 'colle ici un lien d’invitation', autocomplete: 'off', 'aria-label': 'Lien d’invitation' });
  field.addEventListener('input', () => field.setCustomValidity(''));
  return h(
    'form',
    {
      class: 'stack',
      onsubmit: (e: Event) => {
        e.preventDefault();
        const secret = /(?:#\/invite\/)?([A-Za-z0-9_-]{22})\s*$/.exec(field.value.trim())?.[1];
        if (secret && SECRET.test(secret)) location.hash = `#/invite/${secret}`;
        else field.setCustomValidity('Lien d’invitation non reconnu'), field.reportValidity();
      },
    },
    field,
    h('button', { class: 'btn small', type: 'submit', text: 'Ouvrir l’invitation' }),
  );
}

export function chatsView(root: HTMLElement): View {
  let convs: Conversations | null = null;
  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  };
  const timer = setInterval(schedule, 30_000); // "vu il y a…" stays fresh

  function row(c: Conversation): HTMLElement {
    const last = c.last();
    const unread = c.unread();
    const online = contactOnline(c);
    return h(
      'a',
      { class: 'note-row', href: `#/chats/${c.id}` },
      h('span', { class: `presence${online ? ' on' : ''}`, title: online ? 'en ligne' : 'hors ligne' }),
      h(
        'span',
        { class: 'note-main' },
        h('span', { class: 'note-title', text: c.kind === 'group' ? `${c.title()} · ${c.persons().length}` : c.title() }),
        h('span', { class: 'note-excerpt dim', text: last ? `${c.isMine(last) ? 'Toi : ' : c.kind === 'group' ? `${c.nameOf(last.person)} : ` : ''}${last.text}` : 'Aucun message pour l’instant' }),
      ),
      h('span', { class: 'note-date' }, unread ? h('span', { class: 'badge', text: unread }) : h('span', { class: 'dim', text: last ? relativeDate(last.ts) : '' })),
    );
  }

  function render() {
    if (!convs) {
      root.replaceChildren(
        h('h1', { text: 'Discussions' }),
        h('p', { class: 'dim' }, 'Les discussions utilisent ton espace (tes appareils). ', h('a', { href: '#/notes', text: 'Crée-le d’abord dans Notes' }), '.'),
      );
      return;
    }
    const list = convs.list();
    const c = convs;
    const chosen = c.space.profile.ts > 0;
    const nameField = h('input', { class: 'field', value: c.space.displayName, maxlength: 60, 'aria-label': 'Ton nom', autocomplete: 'name' });
    const nameForm = h(
      'form',
      { class: 'row', onsubmit: (e: Event) => (e.preventDefault(), void c.setMyName(nameField.value).then(render)) },
      nameField,
      h('button', { class: 'btn small primary', type: 'submit', text: 'Enregistrer' }),
    );
    root.replaceChildren(
      ...keep(
        h('h1', { text: 'Discussions' }),
        chosen
          ? h('p', { class: 'dim small' }, 'Ton nom : ', h('strong', { text: c.space.displayName }), ' · ', h('a', { href: '#/chats', text: 'modifier', onclick: (e: Event) => { e.preventDefault(); const n = prompt('Ton nom, tel que tes contacts le voient :', c.space.displayName); if (n) void c.setMyName(n).then(render); } }))
          : h('section', { class: 'card' }, h('h2', { text: 'Comment veux-tu apparaître ?' }), h('p', { class: 'dim small', text: 'Ce nom est montré à tes contacts, sur tous tes appareils. Tu pourras le changer.' }), nameForm),
        h('p', { class: 'dim small', text: 'De pair à pair, sans serveur qui garde tes messages : un message arrive quand un de tes appareils et un de ceux de ton contact sont ouverts en même temps.' }),
        h('div', { class: 'row' }, h('a', { class: 'btn primary small', href: '#/chats/new', text: '+ Ajouter un contact' }), convs.contacts().length > 0 && h('a', { class: 'btn small', href: '#/chats/group', text: '+ Nouveau groupe' })),
        list.length === 0 ? h('p', { class: 'dim', text: 'Pas encore de discussion.' }) : h('nav', { class: 'notes' }, ...list.map(row)),
        h('details', null, h('summary', { text: 'J’ai reçu un lien d’invitation' }), pasteInvite()),
      ),
    );
  }

  root.replaceChildren(h('p', { class: 'dim', text: 'Ouverture…' }));
  void getConversations().then((c) => {
    convs = c;
    convs?.addEventListener('change', schedule);
    for (const conv of convs?.map.values() ?? []) conv.sync?.addEventListener('peers', schedule);
    render();
  });

  return {
    destroy() {
      cancelAnimationFrame(frame);
      clearInterval(timer);
      convs?.removeEventListener('change', schedule);
      for (const conv of convs?.map.values() ?? []) conv.sync?.removeEventListener('peers', schedule);
    },
  };
}

/** Picks some of my contacts (those not excluded). */
export function contactPicker(convs: Conversations, exclude: Set<string> = new Set()): { el: HTMLElement; picked: () => Conversation[]; count: number } {
  const choices = convs.contacts().filter((c) => !exclude.has(c.peer!.identity));
  const boxes = choices.map((c) => h('input', { type: 'checkbox', value: c.id, 'aria-label': c.peerName() }));
  const el = choices.length
    ? h('ul', { class: 'files' }, ...choices.map((c, i) => h('li', { class: 'file' }, h('label', { class: 'check' }, boxes[i], ` ${c.peerName()}`))))
    : h('p', { class: 'dim', text: 'Aucun contact à ajouter. Ajoute d’abord des contacts dans Discussions.' });
  return { el, picked: () => choices.filter((_, i) => boxes[i].checked), count: choices.length };
}

export function newGroupView(root: HTMLElement): View {
  void getConversations().then((convs) => {
    if (!convs) return void (location.hash = '#/chats');
    const name = h('input', { class: 'field', placeholder: 'Nom du groupe', maxlength: 80, 'aria-label': 'Nom du groupe' });
    const picker = contactPicker(convs);
    const error = h('p', { class: 'bad' });
    const create = h('button', {
      class: 'btn primary',
      text: 'Créer le groupe',
      onclick: async () => {
        const people = picker.picked();
        if (!people.length) return void (error.textContent = 'Choisis au moins un contact.');
        create.disabled = true;
        const group = await convs.createGroup(name.value, people);
        location.hash = `#/chats/${group.id}`;
      },
    });
    root.replaceChildren(
      h('div', { class: 'row' }, h('a', { class: 'btn small', href: '#/chats', text: '← Discussions' }), h('h1', { text: 'Nouveau groupe' })),
      name,
      h('p', { class: 'dim small', text: 'Tu en seras l’administrateur : toi seul pourras retirer quelqu’un ou renommer le groupe. Chaque membre pourra ajouter ses propres contacts.' }),
      h('h2', { text: 'Avec' }),
      picker.el,
      error,
      create,
    );
  });
  root.replaceChildren(h('p', { class: 'dim', text: 'Ouverture…' }));
  return { destroy() {} };
}

/** Host side: QR code + link, then confirm with the safety number. */
export function inviteHostView(root: HTMLElement): View {
  let host: InviteHost | null = null;
  let request: InviteRequest | null = null;
  const release = keepAwake(); // the guest can only reach a host that stays in the foreground
  let qr: HTMLElement | null = null;
  let url = '';

  async function mount() {
    const convs = await getConversations();
    if (!convs) return void (location.hash = '#/chats');
    host = new InviteHost(convs);
    url = host.link(location.origin);
    qr = qrElement(url);
    host.addEventListener('request', (e) => {
      request = (e as CustomEvent).detail;
      render();
    });
    host.addEventListener('done', (e) => (location.hash = `#/chats/${(e as CustomEvent).detail.conv}`));
    host.addEventListener('expired', () => {
      root.replaceChildren(h('h1', { text: 'Ajouter un contact' }), h('p', { class: 'dim', text: 'L’invitation a expiré (10 min).' }), h('a', { class: 'btn', href: '#/chats', text: 'Retour' }));
    });
    render();
    await host.start(wsUrl, await iceServers());
  }

  function render() {
    if (request) {
      const r = request;
      root.replaceChildren(
        h('h1', { text: 'Ajouter un contact' }),
        h('p', { text: `« ${r.hello.name} » veut te rejoindre (${r.hello.devices.length} appareil${r.hello.devices.length > 1 ? 's' : ''}).` }),
        h('p', { class: 'dim', text: 'Numéro de sécurité : il doit être identique sur son écran. Comparez-le de vive voix ou par un autre moyen.' }),
        h('p', { class: 'safety', text: r.safety }),
        h('div', { class: 'row' }, h('button', { class: 'btn primary small', text: 'Ajouter', onclick: () => void host!.accept() }), h('button', { class: 'btn small', text: 'Refuser', onclick: () => (host!.decline(), (request = null), render()) })),
      );
      return;
    }
    const copy = h('button', { class: 'btn small', text: 'Copier le lien', onclick: async () => (copy.textContent = (await copyText(url)) ? 'Copié' : 'Copie impossible') });
    root.replaceChildren(
      ...keep(
        h('div', { class: 'row' }, h('a', { class: 'btn small', href: '#/chats', text: '← Discussions' }), h('h1', { text: 'Ajouter un contact' })),
        h('p', { class: 'dim', text: 'Fais scanner ce QR code à ton contact, ou envoie-lui le lien. Valable 10 minutes, pour une seule personne.' }),
        qr,
        h('input', { class: 'field link-url', readonly: true, value: url, onfocus: (e: Event) => (e.target as HTMLInputElement).select() }),
        h('div', { class: 'row' }, copy, 'share' in navigator && h('button', { class: 'btn small', text: 'Partager…', onclick: () => void navigator.share({ url }).catch(() => {}) })),
      ),
    );
  }

  root.replaceChildren(h('p', { class: 'dim', text: 'Préparation de l’invitation…' }));
  void mount();
  return {
    destroy() {
      release();
      host?.stop();
    },
  };
}

/** Guest side, opened from `#/invite/<secret>`. */
export function inviteGuestView(root: HTMLElement, secret: string): View {
  let alive = true;
  let close: (() => void) | null = null;
  const show = (...nodes: (Node | string)[]) => alive && root.replaceChildren(h('h1', { text: 'Invitation' }), ...nodes);

  let stall: ReturnType<typeof setTimeout> | undefined;

  async function join(convs: Conversations) {
    show(h('p', { class: 'dim', text: 'Connexion à ton contact…' }));
    stall = setTimeout(
      () =>
        show(
          h('p', { class: 'dim', text: 'Connexion à ton contact…' }),
          h('p', { class: 'warn', text: 'Ton contact ne répond pas : son app doit rester ouverte sur l’écran d’invitation. Une invitation expire après 10 minutes ; si elle est plus ancienne, demande-lui un nouveau lien.' }),
          h('a', { class: 'btn small', href: '#/chats', text: 'Annuler' }),
        ),
      20_000,
    );
    try {
      const { conv, close: c } = await acceptInvite(secret, convs, wsUrl, await iceServers(), (host, safety) => {
        clearTimeout(stall);
        show(
          h('p', { text: `Invitation de « ${host.name} ». Elle doit confirmer de son côté.` }),
          h('p', { class: 'dim', text: 'Numéro de sécurité : vérifiez qu’il est identique sur vos deux écrans.' }),
          h('p', { class: 'safety', text: safety }),
        );
      });
      close = c;
      show(h('p', { class: 'dim', text: 'Accepté. Synchronisation de la discussion…' }));
      const conversation = convs.map.get(conv)!;
      if (!conversation.journal.isMember) {
        await new Promise<void>((resolve) => {
          const check = () => conversation.journal.isMember && (conversation.journal.removeEventListener('envelope', check), resolve());
          conversation.journal.addEventListener('envelope', check);
        });
      }
      c();
      if (alive) location.hash = `#/chats/${conv}`;
    } catch (e) {
      show(h('p', { class: 'bad', text: (e as Error).message === 'declined' ? 'Ton contact a refusé.' : `Impossible de rejoindre : ${(e as Error).message}` }), h('a', { class: 'btn', href: '#/chats', text: 'Retour' }));
    }
  }

  void Promise.all([getSpace(), getConversations()]).then(([space, convs]) => {
    if (!space || !convs) {
      show(h('p', { text: 'Pour rejoindre une discussion, crée d’abord ton espace sur cet appareil.' }), h('a', { class: 'btn primary', href: '#/notes', text: 'Créer mon espace' }));
      return;
    }
    show(h('p', { class: 'dim', text: 'Quelqu’un t’invite à discuter, de pair à pair et chiffré de bout en bout.' }), h('button', { class: 'btn primary', text: 'Continuer', onclick: () => void join(convs) }));
  });

  return {
    destroy() {
      alive = false;
      clearTimeout(stall);
      close?.();
    },
  };
}
